import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API, STATUS_LABELS, formatApiErrorDetail } from "../lib/api";
import {
    Wrench,
    Bike,
    Clock,
    CheckCircle2,
    PackageCheck,
    CircleDot,
    AlertCircle,
    Phone,
    Mail,
    MapPin,
    Clock4,
    MessageSquareQuote,
    Send,
} from "lucide-react";
import StarRating from "../components/StarRating";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const STEPS = [
    { key: "pendente", label: "Recebido", icon: CircleDot, desc: "Moto recebida na oficina" },
    { key: "em_curso", label: "Em Curso", icon: Wrench, desc: "Em reparação" },
    { key: "concluido", label: "Concluído", icon: CheckCircle2, desc: "Pronto a levantar" },
    { key: "entregue", label: "Entregue", icon: PackageCheck, desc: "Entregue ao cliente" },
];

function stepIndex(status) {
    const i = STEPS.findIndex((s) => s.key === status);
    return i === -1 ? 0 : i;
}

function formatDateTime(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleString("pt-PT", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
}

export default function TrackingPage() {
    const { token } = useParams();
    const [data, setData] = useState(null);
    const [settings, setSettings] = useState(null);
    const [error, setError] = useState("");
    const [rating, setRating] = useState(0);
    const [comment, setComment] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [reviewSubmitted, setReviewSubmitted] = useState(false);

    function reload() {
        Promise.all([
            axios.get(`${API}/public/track/${token}`),
            axios.get(`${API}/public/settings`).catch(() => ({ data: null })),
        ])
            .then(([trackRes, setRes]) => {
                setData(trackRes.data);
                setSettings(setRes.data);
            })
            .catch((e) => {
                setError(
                    e.response?.status === 404
                        ? "Link de acompanhamento inválido ou expirado."
                        : "Não foi possível carregar o estado da reparação."
                );
            });
    }

    useEffect(() => {
        reload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    async function submitReview() {
        if (rating < 1) {
            toast.error("Selecione uma classificação de 1 a 5 estrelas.");
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/public/track/${token}/review`, {
                rating,
                comment: comment.trim(),
            });
            toast.success("Obrigado pela sua avaliação!");
            setReviewSubmitted(true);
            reload();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro ao enviar.");
        } finally {
            setSubmitting(false);
        }
    }

    const primaryColor = settings?.cor_primaria && /^#([0-9A-Fa-f]{6})$/.test(settings.cor_primaria)
        ? settings.cor_primaria
        : "#FF3B30";
    const oficinaNome = settings?.nome || "Oficina Moto";
    const mensagem = settings?.mensagem_boas_vindas || "A sua moto está em boas mãos.";

    if (error) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-[#FAFAFA] px-6">
                <div className="w-full max-w-md border border-zinc-200 bg-white p-8 text-center">
                    <AlertCircle className="mx-auto mb-3 h-8 w-8 text-zinc-400" />
                    <h2 className="font-['Outfit'] text-xl font-semibold text-zinc-900">
                        Não encontrado
                    </h2>
                    <p className="mt-2 text-sm text-zinc-600">{error}</p>
                </div>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-[#FAFAFA]">
                <div
                    className="h-6 w-6 animate-spin rounded-full border-2 border-zinc-300"
                    style={{ borderTopColor: primaryColor }}
                />
            </div>
        );
    }

    const currentStep = stepIndex(data.status);

    return (
        <div className="min-h-screen bg-[#FAFAFA]" data-testid="tracking-page">
            {/* Top bar */}
            <header className="border-b border-zinc-200 bg-white">
                <div className="mx-auto flex max-w-3xl items-center gap-3 px-6 py-4">
                    {settings?.logo_url ? (
                        <img
                            src={settings.logo_url}
                            alt={oficinaNome}
                            className="h-10 w-10 rounded-sm border border-zinc-200 object-cover"
                            data-testid="tracking-logo"
                        />
                    ) : (
                        <div
                            className="flex h-9 w-9 items-center justify-center rounded-sm text-white"
                            style={{ backgroundColor: primaryColor }}
                        >
                            <Wrench className="h-4 w-4" />
                        </div>
                    )}
                    <div>
                        <div
                            className="font-['Outfit'] text-base font-semibold tracking-tight text-zinc-900"
                            data-testid="tracking-oficina-nome"
                        >
                            {oficinaNome}
                        </div>
                        <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-400">
                            Acompanhar reparação
                        </div>
                    </div>
                </div>
            </header>

            <main className="mx-auto max-w-3xl px-6 py-10">
                <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-zinc-500">
                    Olá, {data.cliente_primeiro_nome}
                </div>
                <h1
                    className="font-['Outfit'] text-3xl font-semibold tracking-tight text-zinc-900 md:text-4xl"
                    data-testid="tracking-mensagem"
                >
                    {mensagem}
                </h1>
                <p className="mt-2 text-sm text-zinc-600">
                    Acompanhe aqui o estado da reparação em tempo real.
                </p>

                {/* Moto card */}
                <section
                    className="mt-8 border border-zinc-200 bg-white p-6"
                    data-testid="tracking-moto-card"
                >
                    <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-sm border border-zinc-200 bg-zinc-50">
                            <Bike className="h-5 w-5 text-zinc-700" strokeWidth={1.75} />
                        </div>
                        <div>
                            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">
                                Moto
                            </div>
                            <div className="font-['Outfit'] text-lg font-semibold text-zinc-900">
                                {data.moto.marca} {data.moto.modelo}
                            </div>
                        </div>
                    </div>

                    <div className="mt-6 grid grid-cols-2 gap-x-6 gap-y-4 border-t border-zinc-100 pt-5 text-sm md:grid-cols-3">
                        <Info label="Trabalho" value={data.tipo || "—"} testid="tracking-tipo" />
                        <Info
                            label="Data agendada"
                            value={data.data ? `${data.data} · ${data.hora}` : "—"}
                            testid="tracking-data"
                        />
                        <Info
                            label="Previsão de entrega"
                            value={data.data_entrega || "—"}
                            testid="tracking-data-entrega"
                        />
                        <Info
                            label="Chassis"
                            value={data.moto.chassis_mask || "—"}
                            mono
                            testid="tracking-chassis"
                        />
                        {data.descricao ? (
                            <div className="col-span-2 md:col-span-3">
                                <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">
                                    Notas
                                </div>
                                <div className="mt-1 text-sm text-zinc-700">{data.descricao}</div>
                            </div>
                        ) : null}
                    </div>
                </section>

                {/* Timeline */}
                <section
                    className="mt-6 border border-zinc-200 bg-white p-6"
                    data-testid="tracking-timeline"
                >
                    <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">
                        Estado atual
                    </div>
                    <div className="font-['Outfit'] text-2xl font-semibold text-zinc-900">
                        {STATUS_LABELS[data.status] || data.status}
                    </div>
                    <div className="mt-1 flex items-center gap-1.5 text-xs text-zinc-500">
                        <Clock className="h-3.5 w-3.5" />
                        Atualizado em {formatDateTime(data.updated_at)}
                    </div>

                    <ol className="mt-8 space-y-4">
                        {STEPS.map((step, idx) => {
                            const Icon = step.icon;
                            const done = idx < currentStep;
                            const active = idx === currentStep;
                            const pending = idx > currentStep;
                            return (
                                <li
                                    key={step.key}
                                    className="flex items-start gap-4"
                                    data-testid={`tracking-step-${step.key}`}
                                >
                                    <div className="relative flex flex-col items-center">
                                        <div
                                            className={`flex h-9 w-9 items-center justify-center rounded-sm border ${
                                                done
                                                    ? "border-green-200 bg-green-50 text-green-700"
                                                    : pending
                                                    ? "border-zinc-200 bg-zinc-50 text-zinc-400"
                                                    : "text-white"
                                            }`}
                                            style={
                                                active
                                                    ? {
                                                          borderColor: primaryColor,
                                                          backgroundColor: primaryColor,
                                                      }
                                                    : undefined
                                            }
                                        >
                                            <Icon className="h-4 w-4" strokeWidth={1.75} />
                                        </div>
                                        {idx < STEPS.length - 1 ? (
                                            <div
                                                className={`mt-1 h-8 w-px ${
                                                    done ? "bg-green-300" : "bg-zinc-200"
                                                }`}
                                            />
                                        ) : null}
                                    </div>
                                    <div className="pb-3">
                                        <div
                                            className={`font-medium ${
                                                pending ? "text-zinc-400" : "text-zinc-900"
                                            }`}
                                        >
                                            {step.label}
                                            {active ? (
                                                <span
                                                    className="ml-2 inline-block rounded-sm px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.14em] text-white"
                                                    style={{ backgroundColor: primaryColor }}
                                                >
                                                    Agora
                                                </span>
                                            ) : null}
                                        </div>
                                        <div
                                            className={`text-xs ${
                                                pending ? "text-zinc-400" : "text-zinc-600"
                                            }`}
                                        >
                                            {step.desc}
                                        </div>
                                    </div>
                                </li>
                            );
                        })}
                    </ol>
                </section>

                {/* Review section - only when entregue */}
                {data.status === "entregue" ? (
                    <section
                        className="mt-6 border border-zinc-200 bg-white p-6"
                        data-testid="tracking-review-section"
                    >
                        <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">
                            Avaliação
                        </div>
                        {data.review_status === "approved" ? (
                            <div
                                className="flex items-start gap-3 rounded-sm border border-green-200 bg-green-50 p-4"
                                data-testid="review-status-approved"
                            >
                                <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-700" />
                                <div>
                                    <div className="font-medium text-green-900">
                                        Obrigado pelo seu feedback!
                                    </div>
                                    <div className="text-sm text-green-800">
                                        A sua avaliação está publicada — pode vê-la na nossa página de
                                        opiniões.
                                    </div>
                                </div>
                            </div>
                        ) : data.review_status === "pending" || reviewSubmitted ? (
                            <div
                                className="flex items-start gap-3 rounded-sm border border-amber-200 bg-amber-50 p-4"
                                data-testid="review-status-pending"
                            >
                                <MessageSquareQuote className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-700" />
                                <div>
                                    <div className="font-medium text-amber-900">
                                        Avaliação recebida — em moderação.
                                    </div>
                                    <div className="text-sm text-amber-800">
                                        Será revista pela oficina antes de aparecer publicamente.
                                    </div>
                                </div>
                            </div>
                        ) : data.review_status === "rejected" ? (
                            <div className="rounded-sm border border-zinc-200 bg-zinc-50 p-4 text-sm text-zinc-700" data-testid="review-status-rejected">
                                A sua avaliação foi registada. Obrigado.
                            </div>
                        ) : (
                            <div data-testid="review-form">
                                <h3 className="font-['Outfit'] text-xl font-semibold tracking-tight text-zinc-900">
                                    Como correu a sua experiência?
                                </h3>
                                <p className="mt-1 text-sm text-zinc-600">
                                    A sua opinião ajuda outros motociclistas e a equipa a melhorar.
                                </p>

                                <div className="mt-5">
                                    <StarRating
                                        value={rating}
                                        onChange={setRating}
                                        color={primaryColor}
                                        size={32}
                                        testid="review-stars"
                                    />
                                </div>

                                <div className="mt-4">
                                    <Textarea
                                        value={comment}
                                        onChange={(e) => setComment(e.target.value.slice(0, 500))}
                                        rows={3}
                                        placeholder="Conte-nos como foi (opcional, máx. 500 caracteres)"
                                        className="rounded-sm"
                                        data-testid="review-comment-input"
                                    />
                                    <div className="mt-1 text-right text-[11px] text-zinc-400">
                                        {comment.length}/500
                                    </div>
                                </div>

                                <Button
                                    onClick={submitReview}
                                    disabled={submitting || rating < 1}
                                    className="mt-2 rounded-sm text-white hover:opacity-90"
                                    style={{ backgroundColor: primaryColor }}
                                    data-testid="review-submit-btn"
                                >
                                    <Send className="mr-2 h-4 w-4" />
                                    {submitting ? "A enviar..." : "Enviar avaliação"}
                                </Button>
                                <p className="mt-3 text-[11px] text-zinc-500">
                                    A avaliação será revista pela oficina antes de ficar pública.
                                </p>
                            </div>
                        )}
                    </section>
                ) : null}

                {/* Contact card */}
                {(settings?.telefone ||
                    settings?.email ||
                    settings?.morada ||
                    settings?.google_maps_url ||
                    settings?.horario) ? (
                    <section
                        className="mt-6 border border-zinc-200 bg-white p-6"
                        data-testid="tracking-contact"
                    >
                        <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">
                            Contactos
                        </div>
                        <div className="font-['Outfit'] text-lg font-semibold text-zinc-900">
                            {oficinaNome}
                        </div>

                        <div className="mt-4 flex flex-wrap gap-2">
                            {settings.telefone ? (
                                <ContactButton
                                    href={`tel:${settings.telefone.replace(/\s+/g, "")}`}
                                    icon={Phone}
                                    label="Ligar"
                                    sub={settings.telefone}
                                    primary
                                    color={primaryColor}
                                    testid="contact-tel"
                                />
                            ) : null}
                            {settings.google_maps_url ? (
                                <ContactButton
                                    href={settings.google_maps_url}
                                    icon={MapPin}
                                    label="Ver no mapa"
                                    sub={settings.morada || "Abrir Google Maps"}
                                    color={primaryColor}
                                    testid="contact-maps"
                                />
                            ) : settings.morada ? (
                                <ContactButton
                                    href={`https://maps.google.com/?q=${encodeURIComponent(settings.morada)}`}
                                    icon={MapPin}
                                    label="Ver no mapa"
                                    sub={settings.morada}
                                    color={primaryColor}
                                    testid="contact-maps"
                                />
                            ) : null}
                            {settings.email ? (
                                <ContactButton
                                    href={`mailto:${settings.email}`}
                                    icon={Mail}
                                    label="Email"
                                    sub={settings.email}
                                    color={primaryColor}
                                    testid="contact-email"
                                />
                            ) : null}
                        </div>

                        {settings.horario ? (
                            <div className="mt-5 flex items-start gap-2 border-t border-zinc-100 pt-4 text-sm text-zinc-700">
                                <Clock4 className="mt-0.5 h-4 w-4 flex-shrink-0 text-zinc-400" />
                                <div className="whitespace-pre-line" data-testid="contact-horario">
                                    {settings.horario}
                                </div>
                            </div>
                        ) : null}
                    </section>
                ) : null}

                <footer className="mt-10 text-center text-xs text-zinc-500">
                    <Link
                        to="/avaliacoes"
                        className="font-semibold underline-offset-2 hover:underline"
                        style={{ color: primaryColor }}
                        data-testid="footer-reviews-link"
                    >
                        Ver opiniões de outros clientes →
                    </Link>
                    <div className="mt-3">
                        Em caso de dúvida, contacte a oficina. Esta página atualiza-se automaticamente.
                    </div>
                </footer>
            </main>
        </div>
    );
}

function Info({ label, value, mono = false, testid }) {
    return (
        <div data-testid={testid}>
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">
                {label}
            </div>
            <div className={`mt-1 text-sm text-zinc-900 ${mono ? "font-mono" : ""}`}>{value}</div>
        </div>
    );
}

function ContactButton({ href, icon: Icon, label, sub, primary, color, testid }) {
    const styles = primary
        ? { backgroundColor: color, color: "#fff", borderColor: color }
        : { color, borderColor: "#E4E4E7" };
    return (
        <a
            href={href}
            target={href.startsWith("http") ? "_blank" : undefined}
            rel="noreferrer"
            data-testid={testid}
            className={`inline-flex items-center gap-2 rounded-sm border px-4 py-2.5 text-sm font-medium transition-opacity hover:opacity-90 ${
                primary ? "" : "bg-white"
            }`}
            style={styles}
        >
            <Icon className="h-4 w-4" strokeWidth={1.75} />
            <span className="text-left">
                <span className="block font-semibold">{label}</span>
                <span className={`block text-[11px] ${primary ? "text-white/80" : "text-zinc-500"}`}>
                    {sub}
                </span>
            </span>
        </a>
    );
}
